import type { paths } from './generated/schema';

/**
 * Typed fetch over the generated OpenAPI client (ADR-004). Paths, path parameters and JSON
 * response bodies are checked against `generated/schema.d.ts`; an endpoint change that is not
 * regenerated fails `tsc`, not a student.
 *
 * Cookies carry the session (§10.1) — `credentials: 'include'`, never a bearer token. Every request
 * sends `X-Request-Id`; mutating calls also send `Idempotency-Key` (§10.3) unless the caller supplies one.
 */

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

type PathsWithMethod<M extends Method> = {
  [P in keyof paths]: paths[P] extends Record<M, unknown> ? P : never;
}[keyof paths];

type Operation<P extends keyof paths, M extends Method> =
  paths[P] extends Record<M, infer O> ? O : never;

type JsonBody<R> = R extends { content: { 'application/json': infer B } } ? B : undefined;

type SuccessStatus = 200 | 201 | 202;

export type SuccessBody<O> = O extends { responses: infer R }
  ? JsonBody<R[Extract<keyof R, SuccessStatus>]>
  : never;

type PathParams<O> = O extends { parameters: { path: infer P } }
  ? P extends Record<string, string | number>
    ? P
    : never
  : never;

type QueryParams<O> = O extends { parameters: { query?: infer Q } }
  ? [Q] extends [undefined]
    ? never
    : Q extends Record<string, unknown>
      ? { [K in keyof Q]?: string }
      : never
  : never;

type JsonRequest<RB> = RB extends { content: { 'application/json': infer B } } ? B : never;

/** Required body → `B`; optional body → `B | undefined`; `requestBody?: never` → `undefined`. */
type RequestBody<O> = O extends { requestBody: infer RB }
  ? JsonRequest<RB>
  : O extends { requestBody?: infer RB }
    ? [NonNullable<RB>] extends [never]
      ? undefined
      : JsonRequest<NonNullable<RB>> | undefined
    : undefined;

type RequestOptions<O> = {
  signal?: AbortSignal;
  requestId?: string;
  idempotencyKey?: string;
} & ([PathParams<O>] extends [never] ? { params?: undefined } : { params: PathParams<O> }) &
  ([QueryParams<O>] extends [never] ? { query?: undefined } : { query?: QueryParams<O> }) &
  ([undefined] extends [RequestBody<O>] ? { body?: RequestBody<O> } : { body: RequestBody<O> });

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly requestId: string,
    readonly body: unknown,
  ) {
    super(`API ${status} (request ${requestId})`);
    this.name = 'ApiError';
  }
}

export interface ApiClientConfig {
  baseUrl: string;
  fetch?: typeof fetch;
}

function fillPath(template: string, params: Record<string, string | number> | undefined): string {
  return template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${template}`);
    return encodeURIComponent(String(value));
  });
}

/** Untyped view of any `RequestOptions<O>` — the public methods below carry the per-endpoint types. */
interface AnyRequestOptions {
  signal?: AbortSignal | undefined;
  requestId?: string | undefined;
  idempotencyKey?: string | undefined;
  params?: Record<string, string | number> | undefined;
  query?: Record<string, string | undefined> | undefined;
  body?: unknown;
}

export function createApiClient(config: ApiClientConfig) {
  const doFetch = config.fetch ?? ((input, init) => fetch(input, init));
  const base = config.baseUrl.replace(/\/+$/, '');

  async function call<P extends keyof paths, M extends Method>(
    method: M,
    path: P,
    options: AnyRequestOptions,
  ): Promise<SuccessBody<Operation<P, M>>> {
    const requestId = options.requestId ?? crypto.randomUUID();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Request-Id': requestId,
    };
    if (method !== 'get') {
      headers['Idempotency-Key'] = options.idempotencyKey ?? crypto.randomUUID();
    }
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    const query =
      options.query && Object.keys(options.query).length > 0
        ? `?${new URLSearchParams(
            Object.entries(options.query).flatMap(([key, value]) =>
              value === undefined ? [] : [[key, value]],
            ),
          ).toString()}`
        : '';

    const response = await doFetch(`${base}${fillPath(path, options.params)}${query}`, {
      method: method.toUpperCase(),
      headers,
      credentials: 'include',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });

    const text = await response.text();
    const parsed: unknown = text.length > 0 ? JSON.parse(text) : undefined;
    if (!response.ok) throw new ApiError(response.status, requestId, parsed);
    return parsed as SuccessBody<Operation<P, M>>;
  }

  return {
    get<P extends PathsWithMethod<'get'>>(
      path: P,
      options: RequestOptions<Operation<P, 'get'>> = {} as RequestOptions<Operation<P, 'get'>>,
    ) {
      return call('get', path, options);
    },
    post<P extends PathsWithMethod<'post'>>(
      path: P,
      options: RequestOptions<Operation<P, 'post'>>,
    ) {
      return call('post', path, options);
    },
    put<P extends PathsWithMethod<'put'>>(path: P, options: RequestOptions<Operation<P, 'put'>>) {
      return call('put', path, options);
    },
    patch<P extends PathsWithMethod<'patch'>>(
      path: P,
      options: RequestOptions<Operation<P, 'patch'>>,
    ) {
      return call('patch', path, options);
    },
    delete<P extends PathsWithMethod<'delete'>>(
      path: P,
      options: RequestOptions<Operation<P, 'delete'>>,
    ) {
      return call('delete', path, options);
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** Default client for the SPA. Same origin rule as `auth-client.ts`. */
export const api = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? 'http://localhost:3000',
});
